import { useEffect, useMemo, useState } from "react";
import type { components } from "@cntrl/api-client";
import { EmptyState, Icon, MenuButton, type ActionMenuItem } from "@cntrl/ui";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import { useLiveGroup } from "../../../app/realtime/RealtimeProvider.js";
import { useBandContext, useCapability } from "../../../app/bandContext.js";
import { useOpportunities } from "../../../app/hooks/useOpportunities.js";
import {
  useOpportunityMoney, moneyByOpportunity, canSeeOppMoney,
} from "../../../app/hooks/useOpportunityMoney.js";
import { answeredByViewer, feedSortKey, liveFeedItems, liveOpportunities, oppLeftFeed } from "../../dashboard/oppDisplay.js";
import { openRequests, opportunityRow, showInviteRow, showRequestRow } from "../../dashboard/oppCompact.js";
import { OppCompactRow } from "../../dashboard/OppCompactRow.js";
import { ShowRequestDetails } from "../../booking/ShowRequestDetails.js";
import { useShowRequests, type ShowRequestView } from "../../../app/hooks/useShowRequests.js";
import { CalendarEventDetails, type OpenCalendarEvent } from "../../booking/CalendarEventDetails.js";
import { useBandTeam } from "../../../app/hooks/useBandTeam.js";
import { useTasks } from "../../../app/hooks/useTasks.js";
import { TaskListRow } from "../../tasks/TaskListRow.js";
import { taskRowsClassName, taskSubLine } from "../../tasks/taskRow.js";
import { useShows, useVip, useShowInvites, type ShowInviteDto } from "../../../app/hooks/useShows.js";
import { useTravel } from "../../../app/hooks/useTravel.js";
import { useDashboardStats } from "../../../app/hooks/useDashboardStats.js";
import { useMe, useNavigation } from "../../../app/hooks/useSession.js";
import { useMyTasks } from "../../../app/hooks/useTasks.js";
import { useTaskPeople } from "../../tasks/taskPeople.js";
import { NewDealModal } from "../../org/NewDealModal.js";
import { TaskCreateModal } from "../../tasks/TaskCreateModal.js";
import { TodoDetailSheet } from "./TodoDetailSheet.js";
import { MobileCard, CardLoading, CardError } from "./mobileCard.js";
import { DEGRADED, num, shortDate, todayIso, weekdayDate, time12, stayRange } from "./homeFormat.js";
import { useUpcomingShow, useUpcomingShowsWithShared } from "./useUpcomingShow.js";
import { showMoneyById, useShowMoney } from "../../../app/hooks/useMoney.js";
import { CaseChecklist } from "../../roadCases/CaseChecklist.js";
import { ReleaseArtwork } from "../../releases/ReleaseArtwork.js";
import { useDos } from "../../../app/hooks/useDayOfShow.js";
import { callTimes, clock, todayIn, useNextShows, whereLabel } from "../../dashboard/showDay.js";
import { ownSlotId } from "../../../app/showLineup.js";
import { buildLiveActivityPlan, liveActivityStateAt, type LiveActivityContentState } from "../dayOfShow.js";
import { DosLiveCard, DosLiveCardSkeleton } from "./DosLiveCard.js";
import styles from "./MobileHome.module.scss";

// ---- Universal band widgets (band:{id} realtime, already joined by the shell) --------------------------------

type OpportunityDto = components["schemas"]["OpportunityDto"];

/** One row of the merged feed — an in-band opportunity, or a show/hold invite from Booking. Both are dated
 *  the same way and share the one expiry rule, so they interleave by `feedSortKey` (see `oppDisplay`). */
type FeedRow =
  | { kind: "opp"; sort: string; opp: OpportunityDto }
  | { kind: "invite"; sort: string; invite: ShowInviteDto }
  | { kind: "request"; sort: string; request: ShowRequestView };

/**
 * Opportunities — the mobile home's twin of the desktop dashboard's `OpportunitiesBox`: the SAME two-source
 * feed, the SAME compact card (ART-328). Nothing is answered from the card: Details opens the bottom sheet, where
 * the answer lives, and an answered row stays saying "You're in" / "You're out". The creator gets Edit.
 * Money is the separate money-gated sub-resource, fetched ONCE for the whole card and only for a money-team seat.
 */
export function OpportunitiesCard() {
  const opps = useOpportunities();
  const invites = useShowInvites();
  const decides = useCapability("booking:decide");
  const requests = useShowRequests({ enabled: decides });
  const [request, setRequest] = useState<string | null>(null);
  const myPersonId = useMe().data?.person?.personId;
  const { moneyScope } = useBandContext();
  const money = useOpportunityMoney({ enabled: canSeeOppMoney(moneyScope) });
  const moneyFor = moneyByOpportunity(money.data);
  const [editing, setEditing] = useState<OpportunityDto | null>(null);
  const [details, setDetails] = useState<OpenCalendarEvent | null>(null);

  const rows: FeedRow[] = [
    ...liveOpportunities(opps.data?.items ?? undefined)
      .filter((opp) => !oppLeftFeed(opp, myPersonId))
      .map((opp): FeedRow => ({ kind: "opp", sort: feedSortKey(opp), opp })),
    ...liveFeedItems(invites.data?.items ?? undefined)
      .filter((invite) => !answeredByViewer(invite, myPersonId))
      .map((invite): FeedRow => ({ kind: "invite", sort: feedSortKey(invite), invite })),
    // ART-327 — a show request waiting on the team, for the people who decide it (Mgmt / the artist AV).
    ...openRequests(requests.data?.requests)
      .map((request): FeedRow => ({ kind: "request", sort: feedSortKey({ date: request.date }), request })),
  ].sort((a, b) => a.sort.localeCompare(b.sort));

  // Either source failing fails the CARD — a half-rendered feed silently drops rows you're meant to answer.
  const loading = opps.isLoading || invites.isLoading;
  const failed = opps.isError ? opps.error : invites.isError ? invites.error : null;

  // Empty ⇒ the card is not shown at all (ART-227, mobile): creating one is the shared + beside the greeting.
  if (!loading && !failed && rows.length === 0) return null;
  const title = rows.length ? `Opportunities · ${rows.length}` : "Opportunities";

  return (
    <MobileCard title={title} bare>
      {loading ? (
        <CardLoading />
      ) : failed ? (
        <CardError error={failed} onRetry={() => { void opps.refetch(); void invites.refetch(); }} />
      ) : (
        <ul className={styles.oppFeed}>
          {rows.map((row) => {
            if (row.kind === "request") {
              const id = row.request.offerId!;
              return (
                <OppCompactRow
                  key={`request:${id}`}
                  {...showRequestRow(row.request)}
                  actionLabel="Details"
                  onAction={() => setRequest(id)}
                />
              );
            }
            if (row.kind === "opp") {
              const id = row.opp.opportunityId!;
              const m = opportunityRow(row.opp, myPersonId, moneyFor[id]?.moneyCents);
              return (
                <OppCompactRow
                  key={`opp:${id}`}
                  {...m}
                  actionLabel={m.side === "creator" ? "Edit" : "Details"}
                  onAction={() => m.side === "creator"
                    ? setEditing(row.opp)
                    : setDetails({ kind: "opportunity", id, anchor: null })}
                />
              );
            }
            const id = row.invite.showId!;
            return (
              <OppCompactRow
                key={`show:${id}`}
                {...showInviteRow(row.invite)}
                actionLabel="Details"
                onAction={() => setDetails({ kind: "show", id, anchor: null })}
              />
            );
          })}
        </ul>
      )}
      {editing && <NewDealModal opp={editing} title="Edit opportunity" onClose={() => setEditing(null)} />}
      {details && <CalendarEventDetails open={details} onClose={() => setDetails(null)} />}
      {request && <ShowRequestDetails offerId={request} onClose={() => setRequest(null)} />}
    </MobileCard>
  );
}

/**
 * To-Do — the phone's half of the dashboard To-do card, and held to the same spec: still-OPEN tasks only,
 * the release named under a rollout task, and the due date on the right turning into a crimson "Overdue".
 * Live via `task-changed`.
 *
 * It is a SEPARATE component from the desktop `TasksCard` because the mobile home is its own widget tree
 * (`MobileHome`), not the shared `DashboardScreen` — so anything the spec says about the To-do card has to be
 * satisfied twice. Two things it still lacks, tracked in the audit: a row does not open the task drawer, and
 * there is no ＋ New.
 *
 * <b>Reused, filtered, by the mobile Releases screen</b> (ART-189): `releaseId` scopes the list to one
 * release and drops the redundant release name from each sub-line (the card is already about that release);
 * `emptyText` opts that surface into a designed empty message ("No items on list") where the home hides the
 * card entirely (ART-227); `onAddTask` renders a dashed "+ Add task" footer (the Releases screen has no
 * page-level create button, so the card carries one). No prop ⇒ the home's exact prior behaviour.
 */
export function TodoCard({
  releaseId,
  emptyText,
  onAddTask,
}: { releaseId?: string; emptyText?: string; onAddTask?: () => void } = {}) {
  // Open only — the same reason as the desktop card: a to-do list showing finished work is not one.
  const q = useTasks(releaseId ? { openOnly: true, releaseId } : { openOnly: true });
  const rows = q.data?.items ?? [];
  // The people directory the task read carries — resolves the assigner→assignee faces (ART-179). The mobile
  // home always has a band mounted, so `useTaskPeople` (band seat list + directory) is safe here.
  const people = useTaskPeople(q.data?.people ?? undefined);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  // Re-read the open task from the live list each render so an optimistic status change (from the sheet)
  // reflects in place; the sheet closes on its own or when the row leaves the open set.
  const openTask = rows.find((t) => t.taskId === openTaskId) ?? null;
  const empty = !q.isLoading && !q.isError && rows.length === 0;
  // Empty ⇒ the card is not shown at all (ART-227, mobile): To-Do only appears when there is open work.
  // Creating a task is the shared + beside the greeting on the home. A caller that passes `emptyText` or an
  // `onAddTask` (the Releases screen) keeps the card so its message / add button stays reachable.
  if (empty && !emptyText && !onAddTask) return null;
  return (
    <MobileCard title="To-Do">
      {q.isLoading ? (
        <CardLoading />
      ) : q.isError ? (
        <CardError error={q.error} onRetry={() => void q.refetch()} />
      ) : empty ? (
        emptyText ? <p className={styles.todoEmpty}>{emptyText}</p> : null
      ) : (
        // The app's one task row (ART-343): the same design as every other task list, status edge included.
        <ul className={taskRowsClassName(styles.todoRows)}>
          {rows.slice(0, 5).map((t) => (
            <TaskListRow
              key={t.taskId}
              task={t}
              people={people}
              // Scoped to a release, the card already names it — drop the redundant release title (Ryan, ART-189).
              sub={taskSubLine(t, { release: !releaseId })}
              // The whole row taps open the detail sheet (ART-179) — "open and show all the info".
              onOpen={() => setOpenTaskId(t.taskId ?? null)}
            />
          ))}
        </ul>
      )}
      {onAddTask && (
        <button type="button" className={styles.addTask} onClick={onAddTask}>
          + Add task
        </button>
      )}
      {openTask && (
        <TodoDetailSheet task={openTask} people={people} onClose={() => setOpenTaskId(null)} />
      )}
    </MobileCard>
  );
}

/** Upcoming (shows) — the next few confirmed shows, guest dates included. Each row opens that show's Day of
 *  Show (ART-251), via `useNavigate` rather than `<Link>` (which throws in the card's isolated unit render). Live
 *  via `schedule-changed`. */
export function UpcomingCard() {
  const q = useUpcomingShowsWithShared();
  const navigate = useNavigate();
  const rows = q.items;
  return (
    <MobileCard title="Upcoming">
      {q.isLoading ? (
        <CardLoading />
      ) : q.isError ? (
        <CardError error={q.error} onRetry={q.refetch} />
      ) : rows.length === 0 ? (
        <EmptyState title="No upcoming shows" description="Confirmed dates appear here." />
      ) : (
        <ul className={styles.rows}>
          {rows.slice(0, 3).map((s) => (
            <li key={s.showId} className={styles.row}>
              <button
                type="button"
                className={styles.upcomingButton}
                onClick={() => s.showId && void navigate({ to: "/dos", search: { show: s.showId, via: "link" } })}
              >
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>
                    {s.venueName || "Show"}
                    {s.city ? ` · ${s.city}` : ""}
                  </span>
                </div>
                <span className={styles.rowDate}>{shortDate(s.date)}</span>
                <Icon icon={ChevronRight} size={16} className={styles.upcomingChevron} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </MobileCard>
  );
}

type TravelItemDto = components["schemas"]["TravelItemDto"];
/** The four text slots a Travel row is built from (Dev Reference, ART-178). */
type TravelRowParts = { type: string; title: string; topSub: string | null; meta: string | null };

/** The slots for a flight / hotel / ground leg. Only the slot CONTENTS change per kind — there is no per-type
 *  layout. The TITLE always carries the answer (the route for a flight, the property for a hotel), because it
 *  is the one line guaranteed to survive truncation; everything else supports it. */
function travelRowParts(
  t: TravelItemDto,
  showDate: string | null | undefined,
  venue: string | null | undefined,
): TravelRowParts {
  // Every row belongs to the one upcoming show, so its meta line is that show's date + venue.
  const meta = [showDate ? weekdayDate(showDate) : null, venue?.trim() || null].filter(Boolean).join(" · ") || null;
  if (t.kind === "flight") {
    // A missing airport renders `?`, never blank; the airline/number and the departure time make the top-sub.
    const airlineNo = [t.airline, t.flightNumber].filter(Boolean).join(" ").trim();
    return {
      type: "FLIGHT",
      title: `${t.departAirport ?? "?"} → ${t.arriveAirport ?? "?"}`,
      topSub: [airlineNo || null, time12(t.departLocal) || null].filter(Boolean).join(" · ") || null,
      meta,
    };
  }
  if (t.kind === "hotel") {
    // Hotel with no parsed name falls back to the literal "Lodging" — the one case the title can't answer.
    return { type: "HOTEL", title: t.propertyName?.trim() || "Lodging", topSub: stayRange(t.checkIn, t.checkOut), meta };
  }
  // Ground / any other kind — a real leg in our data that the Dev Reference's Bus row stands in for. Same
  // one-row anatomy; the TYPE pill names the kind and the address is the answer.
  return { type: (t.kind || "travel").toUpperCase(), title: t.address?.trim() || "Ground", topSub: null, meta };
}

/** Is this leg the signed-in user's? Assigned-to-all (and an unrecorded assignment) is band-wide and shows to
 *  everyone; a leg pinned to specific people shows only to them — the Dev Reference's personal digest, "two
 *  members on the same tour see different modules". Identity not yet resolved ⇒ show, never hide. */
function travelForMe(t: TravelItemDto, myPersonId: string | null | undefined): boolean {
  if (!myPersonId || t.assignedAll) return true;
  const ids = t.personIds ?? [];
  return ids.length === 0 || ids.includes(myPersonId);
}

/**
 * Travel — the next show's flights, hotels and ground in the Dev Reference row anatomy (ART-178): a crimson
 * TYPE pill + top-sub line, a title that carries the answer (the route / the property), and a meta line naming
 * the date and venue. Personal, not band-wide — only legs matched to the signed-in user. Each row is its own
 * paper card and taps through to that show's Logistics, where the full flight strip and hotel call-out live;
 * the row never expands.
 *
 * The bus bunk is deliberately NOT a row here (Ryan, 2026-09-16): the Pro My Bunk card already carries the
 * bunk on the home, so a bus row would show the same bunk twice. Travel stays flights, hotels and ground.
 *
 * Bare (ART-178) — like Opportunities, no white box around the SECTION and no "more info" button (Ryan); each
 * ITEM is a card. Capped at three; more than three reads "Travel · 3 of N" in the eyebrow, the only signal
 * anything is hidden (there is no "show all"). Two-step: resolve the upcoming show, then its travel (`useTravel`
 * is gated on the id).
 */
export function TravelCard() {
  const up = useUpcomingShow();
  const q = useTravel(up.showId);
  const myPersonId = useMe().data?.person?.personId;
  const navigate = useNavigate();

  const mine = (q.data?.items ?? []).filter((t) => travelForMe(t, myPersonId));
  const shown = mine.slice(0, 3);
  const title = mine.length > 3 ? `Travel · 3 of ${mine.length}` : "Travel";

  const loading = up.isLoading || (!!up.showId && q.isLoading);
  const failed = up.isError ? up.error : q.isError ? q.error : null;

  return (
    <MobileCard title={title} bare>
      {loading ? (
        <CardLoading />
      ) : failed ? (
        <CardError error={failed} onRetry={() => { up.refetch(); void q.refetch(); }} />
      ) : !up.showId || shown.length === 0 ? (
        <EmptyState title="No travel arrangements yet" />
      ) : (
        <ul className={styles.travelList}>
          {shown.map((t, i) => (
            <TravelRow
              key={t.travelItemId ?? i}
              parts={travelRowParts(t, up.show?.date, up.show?.venueName)}
              onOpen={() => void navigate({ to: "/shows/$showId/logistics", params: { showId: up.showId! } })}
            />
          ))}
        </ul>
      )}
    </MobileCard>
  );
}

/** One Travel row — the Dev Reference's single shape for every kind, rendered as a paper card. The whole card
 *  is the tap target (it navigates to Logistics via `useNavigate`, not `<Link>`, which throws in the card's
 *  isolated unit render); the chevron is decorative, not a separate control. Every text line is one-line
 *  ellipsis, with `min-width:0` carried down the flex chain so a long value truncates rather than pushing the
 *  row off-screen — the named rebuild bug in the Dev Reference. */
function TravelRow({ parts, onOpen }: { parts: TravelRowParts; onOpen: () => void }) {
  return (
    <li className={styles.travelRow}>
      <button type="button" className={styles.travelButton} onClick={onOpen}>
        <div className={styles.travelBody}>
          <div className={styles.travelTop}>
            <span className={styles.travelType}>{parts.type}</span>
            {parts.topSub && <span className={styles.travelTopSub}>{parts.topSub}</span>}
          </div>
          <span className={styles.travelTitle}>{parts.title}</span>
          {parts.meta && <span className={styles.travelMeta}>{parts.meta}</span>}
        </div>
        <Icon icon={ChevronRight} size={20} className={styles.travelChevron} />
      </button>
    </li>
  );
}

// ---- Show-scoped preview widgets (join the show:{id} group so they live-update) ------------------------------

/** Ticket sales (ART-252) — EVERY upcoming confirmed date, one line each (PO 2026-10-02: "this box could have 100
 *  dates in here at one time"): `sold/capacity  Venue · City, ST   View DOS →`. Sold is the settlement door count
 *  the server composes onto the money list (presale + walk-up + VIP — `SettlementTickets.Sold`), looked up by id;
 *  a date with no count yet reads "—" rather than a fabricated 0. Money-gated on the server's home list, so the
 *  money read is always entitled here. Scrolls past a handful of rows. It joins no per-show realtime group (a
 *  run of 100 dates would be 100 subscriptions); a count typed on a worksheet lands on the next focus/refetch. */
export function TicketSalesCard() {
  const today = todayIso();
  const shows = useShows({ status: "confirmed", sort: "date", from: today, limit: 100 });
  const money = useShowMoney({ status: "confirmed", from: today, limit: 200 }, true);
  const navigate = useNavigate();
  const rows = shows.data?.items ?? [];
  const sold = showMoneyById(money.data);
  return (
    <MobileCard title="Ticket sales">
      {shows.isLoading || money.isLoading ? (
        <CardLoading />
      ) : shows.isError ? (
        <CardError error={shows.error} onRetry={() => void shows.refetch()} />
      ) : money.isError ? (
        <CardError error={money.error} onRetry={() => void money.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No upcoming shows" description="Ticket counts appear once a show is confirmed." />
      ) : (
        <ul className={styles.ticketRows} data-scroll={rows.length > 6 || undefined}>
          {rows.map((s) => {
            const count = s.showId ? sold.get(s.showId)?.ticketsSold : null;
            const where = [s.city, s.state].filter(Boolean).join(", ");
            return (
              <li key={s.showId}>
                <button
                  type="button"
                  className={styles.ticketButton}
                  onClick={() => s.showId && void navigate({ to: "/dos", search: { show: s.showId, via: "link" } })}
                >
                  <span className={styles.ticketCount}>
                    {count == null ? "—" : num(count)}
                    {s.capacity != null && <span className={styles.ticketCap}>/{num(s.capacity)}</span>}
                  </span>
                  <span className={styles.ticketWhere}>
                    {s.venueName || "Show"}
                    {where ? ` · ${where}` : ""}
                  </span>
                  <span className={styles.ticketGo}>View DOS →</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </MobileCard>
  );
}

/** VIP List — the upcoming show's VIP block. Live via the `show:{id}` group.*/
export function VipCard() {
  const up = useUpcomingShow();
  return (
    <MobileCard title="VIP">
      {up.isLoading ? (
        <CardLoading />
      ) : up.isError ? (
        <CardError error={up.error} onRetry={up.refetch} />
      ) : !up.showId ? (
        <EmptyState title="No upcoming show" description="VIP details for your next show appear here." />
      ) : (
        <VipInner showId={up.showId} />
      )}
    </MobileCard>
  );
}

function VipInner({ showId }: { showId: string }) {
  useLiveGroup(`show:${showId}`);
  const q = useVip(showId);
  const vip = q.data;
  if (q.isLoading) return <CardLoading />;
  if (q.isError) return <CardError error={q.error} onRetry={() => void q.refetch()} />;
  const empty = !vip?.entryTime && !vip?.source && !vip?.list;
  if (empty) return <EmptyState title="No VIP set" description="Entry time, source, and the VIP list show here." />;
  return (
    <div className={styles.stack}>
      {vip?.entryTime && <p className={styles.line}>Entry {vip.entryTime.slice(0, 5)}</p>}
      {vip?.source && <p className={styles.line}>Source: {vip.source}</p>}
      {vip?.list && <p className={styles.vipList}>{vip.list}</p>}
    </div>
  );
}

// ---- Merch / analytics sections moved to their own files ----------------------------------------------------
// The consolidated Merch section (ART-182) lives in MerchSection.tsx and the Audience section (ART-181, which
// replaced the old `your_numbers` card) in AudienceCard.tsx. The retired mobile-home cards — merch_cut,
// pos_sales, the fan-signup merch_qr (ART-180) and the production input_list (ART-180) — were removed with
// their nav keys; `useProduction` / `useSignupQr` / `useMerchRollup` are no longer imported here.

/** Case Checklist — the band's road cases and where each one is (Home · In venue · On truck), live across the
 * crew; scan mode, reset, the case sheet and adding a case all happen right here (ART-253, the legacy PACKING card).
 * The body is the shared `CaseChecklist`, the same one `/road-cases` renders. */
export function CaseChecklistCard() {
  return (
    <MobileCard title="Case Checklist">
      <CaseChecklist />
    </MobileCard>
  );
}

// ---- shared row sub-parts ------------------------------------------------------------------------------------

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.figure}>
      <span className={styles.figureValue}>{value}</span>
      <span className={styles.figureLabel}>{label}</span>
    </div>
  );
}

// ---- show day (ART-126) --------------------------------------------------------------------------------

/**
 * SHOWTIME — the show-DAY hero, and the only home card that renders nothing at all on most days.
 *
 * <b>Show-day-only is the whole design, not a filter on top of one.</b> `UpcomingCard` already answers "what
 * is next"; a second permanent card restating it would be noise on the 300 days a year the band is not
 * playing. This one appears on the morning of a date and states the three times the day actually runs on —
 * load-in, doors, stage — over the venue it is at. Returning `null` (rather than an empty state) is what
 * lets it sit high in the stack without costing anything: on a non-show day the home reads exactly as it did
 * before this card existed.
 *
 * <b>The call times come from `callTimes`</b>, shared with the desktop heroes, because load-in and stage are
 * per ACT and resolving them off the raw run-of-show rows puts a changeover's duration under "Showtime" —
 * a defect the desktop hero already found and recorded. This card must not re-derive them.
 *
 * Money-free by construction: a call sheet is schedule data, which is why every band seat gets this card and
 * the settlement card beside it is gated.
 */
export function ShowtimeCard() {
  const { shows, isLoading, isError, error, refetch } = useNextShows();
  // `useNextShows` is `from: today`, so a show TODAY is items[0] until it passes. Comparing the date rather
  // than trusting the position keeps a band with no date today from promoting next week's show into a hero.
  const next = shows[0];
  // The VENUE's today (`todayIn`), not UTC's (which disagrees with every evening behind UTC — precisely load-in
  // time) nor the phone's (a phone in another zone would flip the card around midnight).
  const isShowDay = !!next?.date && next.date.slice(0, 10) === todayIn(next.timezone);
  const dos = useDos(isShowDay ? (next.showId ?? "") : "");
  const { loadIn, doors, stage } = callTimes(dos.data);
  const navigate = useNavigate();
  const seats = useBandTeam().data?.items ?? [];
  // The band's own name, off the owner seat (the "Artist IS the band").
  const bandName = seats.find((st) => st.isOwner)?.displayName ?? "";

  // The Live Activity, drawn here (PO, ART-284): the SAME plan the phone registers for the lock screen, so the
  // card and the widget cannot disagree. No set on the run of show ⇒ no plan, and the call-time card below stands.
  const show = dos.data?.show;
  const plan = useMemo(() => {
    if (!show?.showId || !show.date) return null;
    return buildLiveActivityPlan({
      timezone: show.timezone,
      dateIso: show.date,
      items: dos.data?.runOfShow ?? [],
      ownSlotId: ownSlotId(show.lineup, show.bandId),
      // The band's name labels its own unlabelled set, as on the lock screen.
      attributes: { showId: show.showId, bandLabel: bandName, venue: show.venueName ?? "", city: show.city ?? "" },
    });
  }, [show, dos.data?.runOfShow, bandName]);
  const now = useNow(plan ? 1000 : null);

  // Nothing on the books today ⇒ NOTHING, not an empty state. A hero that says "no show today" every day is
  // a permanent card telling you what you already know.
  if (isLoading || (!isShowDay && !isError)) return null;

  // A show day whose Day of Show data has not landed yet: the live card's own dark skeleton, never the white
  // call-times card it would otherwise flash on its way to the dark one.
  if (!isError && dos.isLoading) return <DosLiveCardSkeleton />;

  // Up to the end of the final set it is the widget; after it (the card has ended on the lock screen too) the
  // day's call times remain.
  if (!isError && plan && now < (plan.endEpochMs ?? 0)) {
    return (
      <DosLiveCard
        state={liveActivityStateAt(plan, now) as LiveActivityContentState}
        bandLabel={bandName || show?.venueName || "Day of Show"}
        nowMs={now}
        onOpen={() => next.showId && void navigate({ to: "/dos", search: { show: next.showId, via: "link" } })}
      />
    );
  }

  return (
    <MobileCard title="Showtime · Today">
      {isError ? (
        <CardError error={error} onRetry={refetch} />
      ) : (
        <>
          <p className={styles.showWhere}>{whereLabel(next)}</p>
          <div className={styles.figures}>
            <Figure label="Load-in" value={clock(loadIn) ?? DEGRADED} />
            <Figure label="Doors" value={clock(doors) ?? DEGRADED} />
            <Figure label="Stage" value={clock(stage) ?? DEGRADED} />
          </div>
        </>
      )}
    </MobileCard>
  );
}

/** The wall clock, re-read every `everyMs` while it is non-null — what the live card's countdown and bar run on. */
function useNow(everyMs: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (everyMs == null) return;
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

// ---- Greeting + release (the reference's artist-tier home lead) ----------------------------------------------

/**
 * The computed greeting — never stored (Dashboard dev reference): a time-of-day prefix, the name in caps,
 * and a sub-line that states what is next and what is waiting on you.
 *
 * Two reference rulings land here. The "· N HOLDS" chip beside the name is CUT (change request, Aug
 * 2026) — a pending count belongs in the sub-line, one signal not two. And the name follows this app's
 * own identity rule: an OWNER seat greets with the BAND's name, because the Artist IS the band; every
 * other seat greets with their first name.
 */
export function GreetingCard() {
  const me = useMe();
  const { bandId } = useBandContext();
  const upcoming = useShows({ status: "confirmed", sort: "date", from: todayIso() });
  const tasks = useMyTasks();

  // The shared create control (ART-227) — the one + on the mobile home, beside the greeting; the per-card +
  // on To-Do and Opportunities are gone. Each option is gated by who may actually create that thing:
  //   • Task — everyone whose home stack carries the To-Do widget (band:read), unchanged from the old card +.
  //   • Opportunity — `opportunities:create` (Artist/AV + the three roster orgs, PO 2026-09); the server
  //     gates the create endpoint on the SAME token, so the offered option and the accepted POST cannot drift.
  // Empty items ⇒ MenuButton renders nothing, so a seat that may create neither gets no button at all.
  const keys = useNavigation(`band:${bandId}`).data?.mobile?.home ?? [];
  const canCreateOpp = useCapability("opportunities:create");
  const [taskOpen, setTaskOpen] = useState(false);
  const [oppOpen, setOppOpen] = useState(false);
  const createItems: ActionMenuItem[] = [
    keys.includes("todo") ? { label: "New task", onSelect: () => setTaskOpen(true) } : null,
    canCreateOpp ? { label: "New opportunity", onSelect: () => setOppOpen(true) } : null,
  ].filter((x): x is ActionMenuItem => x !== null);

  const person = me.data?.person;
  const seat = me.data?.seats?.find((s) => s.kind === "band" && s.bandId === bandId);
  // Owner ⇒ the band name (the Artist IS the band); anyone else ⇒ their first name.
  const name = (seat?.isOwner ? seat.displayName : person?.firstName || person?.displayName) ?? "";

  const hour = new Date().getHours();
  const prefix = hour < 12 ? "Good morning," : hour < 18 ? "Good afternoon," : "Good evening,";

  // The sub-line: what's next, then what needs you. Each half is omitted when it has nothing to say —
  // never a fabricated "0 things need you".
  const next = upcoming.data?.items?.[0];
  const days = next?.date ? daysFromToday(next.date) : null;
  const eventLabel = next ? (KIND_WORDS[(next.kind ?? "show").toLowerCase()] ?? "show") : null;
  const whenPart =
    next == null || days == null
      ? null
      : days <= 0
        ? `${capitalize(eventLabel!)} today`
        : `Next ${eventLabel} in ${days} day${days === 1 ? "" : "s"}`;

  const openForMe = (tasks.data?.items ?? []).filter(
    (t) => t.bandId === bandId && t.status !== "done" && t.status !== "archived",
  ).length;
  const needPart =
    openForMe === 0 ? null : openForMe === 1 ? "1 thing needs you" : `${openForMe} things need you`;
  const sub = [whenPart, needPart].filter(Boolean).join(" · ") || "Nothing on the books";

  return (
    <section className={styles.greeting} aria-label="Greeting">
      <div className={styles.greetText}>
        <p className={styles.greetPrefix}>{prefix}</p>
        <p className={styles.greetName}>{name.toUpperCase()}</p>
        <p className={styles.greetSub}>{sub}</p>
      </div>
      <MenuButton
        items={createItems}
        menuLabel="Create"
        variant="accent"
        size="sm"
        placement="below"
        className={styles.greetAdd}
      >
        <Icon icon={Plus} size={16} />
      </MenuButton>
      {taskOpen && (
        <TaskCreateModal onClose={() => setTaskOpen(false)} onCreated={() => setTaskOpen(false)} />
      )}
      {oppOpen && <NewDealModal title="New opportunity" onClose={() => setOppOpen(false)} />}
    </section>
  );
}

/** The event nouns the greeting's sub-line uses; unknown kinds fall back to "show". */
const KIND_WORDS: Record<string, string> = {
  show: "show", festival: "festival", rehearsal: "rehearsal", promo: "promo day",
};
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Whole days from today to a wall-clock date; 0 = today. */
function daysFromToday(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return null;
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  return Math.round(
    (then.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000,
  );
}

/**
 * Most recent release — the artist tier's rollout snapshot, facelifted (ART-177): the cover art, the title,
 * a `SINGLE · May 8 · Day n` meta line, and a 2×2 grid of stat tiles (Spotify streams / Playlist reach /
 * Landing · MTD / Pop score). ONE card, not a carousel — the reference's Aug 2026 change request kills the
 * lone swipe dot for a single release, which is the case this read serves.
 *
 * <b>No sparklines, deliberately.</b> The mock shows a mini trend on each tile, but `ReleaseStatStrip` is
 * plain scalars — the dashboard payload carries NO series at the RELEASE grain (the only series anywhere is
 * `TrendingItem.series`, and the Spotify deltas are ARTIST-grain, a different measurement). Drawing a flat or
 * borrowed line would fabricate a trend the data does not support, so the tiles show the real number and the
 * graphs wait on a stored release-grain series (a scoped backend follow-up).
 */
export function RecentReleaseCard() {
  const q = useDashboardStats();
  const rr = q.data?.recentRelease ?? null;
  const stats = rr?.stats;
  const meta = [rr?.releaseType ? rr.releaseType.toUpperCase() : null, shortDate(rr?.releaseDate)]
    .filter(Boolean)
    .join(" · ");

  return (
    <MobileCard title="Most recent">
      {q.isLoading ? (
        <CardLoading />
      ) : q.isError ? (
        <CardError error={q.error} onRetry={() => void q.refetch()} />
      ) : rr == null ? (
        <EmptyState
          title="No releases yet"
          description="Start one on desktop and its rollout numbers track here."
        />
      ) : (
        <>
          <div className={styles.rrHead}>
            {/* The shared artwork primitive, so the cover follows the one precedence every other cover surface
                uses — the band's uploaded cover (`coverMediaId`), then the hotlink, then the release's gradient.
                This card read `coverImageUrl` alone, so an uploaded cover never showed on the phone. */}
            <ReleaseArtwork
              size="tile"
              className={styles.rrCover}
              seed={rr.releaseId ?? rr.title ?? ""}
              mediaId={rr.coverMediaId}
              imageUrl={rr.coverImageUrl}
              lazy={false}
            />
            <div className={styles.rrHeadText}>
              <h3 className={styles.rrTitle}>{rr.title ?? "Untitled"}</h3>
              <p className={styles.rrMeta}>
                {meta && <span>{meta}</span>}
                {stats?.dayInCycle != null && <span className={styles.pill}>Day {stats.dayInCycle}</span>}
              </p>
            </div>
          </div>
          <div className={styles.rrTiles}>
            <RrTile label="Spotify streams" value={num(stats?.spotifyStreams)} />
            <RrTile label="Playlist reach" value={num(stats?.playlistReach)} />
            <RrTile label="Landing · MTD" value={num(stats?.landingVisitsMtd)} />
            <RrTile
              label="Pop score"
              value={stats?.spotifyPopularity != null ? `${stats.spotifyPopularity}/100` : DEGRADED}
            />
          </div>
        </>
      )}
    </MobileCard>
  );
}

/** A recent-release stat tile — label + value on a tinted panel (ART-177). No sparkline: see the card note. */
function RrTile({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.rrTile}>
      <span className={styles.rrTileLabel}>{label}</span>
      <span className={styles.rrTileValue}>{value}</span>
    </div>
  );
}
